import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { api } from '../lib/api.js';
import { DestinationFeature } from '@mpr/shared';
import { DestinationFilter } from './DestinationPicker.js';
import { pageDestinationId, pageIdOf } from '../lib/destinations.js';
import { useI18n } from '../i18n/LanguageProvider.js';
import { en } from '../i18n/translations.js';
import { ErrorNotice } from './ErrorNotice.js';
import { Icon } from './Icons.js';

/**
 * Meta's word for a template's state, in the reader's language where we have
 * one and in Meta's own where we do not.
 *
 * The status enum is Meta's and it grows: beyond approved, pending and
 * rejected there are appeals, pauses and disablements, and more can appear
 * without warning. Translating what we know and passing the rest through keeps
 * an unfamiliar state readable instead of turning it into "utility.status.PAUSED".
 */
function statusLabel(status: string, t: (key: never) => string): string {
  const key = `utility.status.${status}`;
  return key in en ? t(key as never) : status;
}

/** Count the distinct `{{n}}` placeholders in a piece of body text. */
function placeholders(text: string): number {
  const found = new Set<string>();
  for (const match of text.matchAll(/\{\{\s*(\d+)\s*\}\}/g)) {
    if (match[1]) found.add(match[1]);
  }
  return found.size;
}

/**
 * Utility message templates for one Page.
 *
 * These are what makes it possible to answer a Messenger thread after Meta's
 * 24-hour window has closed. Everything about the screen follows from two facts
 * about them:
 *
 *   • **Meta owns them.** They are never copied into our database. What Meta
 *     says the status is *is* the status, and a cached "approved" that had since
 *     been revoked would be wrong at precisely the moment it mattered.
 *
 *   • **Meta reviews them, usually in seconds.** So creating one shows its
 *     verdict immediately rather than promising to tell the operator later, and
 *     a rejection shows Meta's own reason instead of a generic failure.
 *
 * The placeholder count is enforced here as well as on the server. Meta answers
 * a mismatch with a bare invalid-parameter error that names nothing, and the
 * operator would be left guessing which `{{2}}` it disliked.
 */
export function UtilityTemplatesPanel() {
  const { t } = useI18n();
  const queryClient = useQueryClient();

  const [pageId, setPageId] = useState('');
  const [name, setName] = useState('');
  const [language, setLanguage] = useState('ar');
  const [headerText, setHeaderText] = useState('');
  const [bodyText, setBodyText] = useState('');
  const [examples, setExamples] = useState<string[]>([]);

  const templates = useQuery({
    queryKey: ['message-templates', pageId],
    queryFn: () => api.messageTemplates(pageId),
    enabled: Boolean(pageId),
  });

  const needed = placeholders(bodyText);
  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: ['message-templates', pageId] });

  const create = useMutation({
    mutationFn: () =>
      api.createMessageTemplate(pageId, {
        name,
        language,
        bodyText,
        bodyExamples: examples.slice(0, needed),
        headerText: headerText.trim() || null,
      }),
    onSuccess: () => {
      setName('');
      setHeaderText('');
      setBodyText('');
      setExamples([]);
      void invalidate();
    },
  });

  const remove = useMutation({
    mutationFn: (templateName: string) => api.deleteMessageTemplate(pageId, templateName),
    onSuccess: invalidate,
  });

  const rows = templates.data?.templates ?? [];
  const ready =
    Boolean(pageId) &&
    name.trim().length > 0 &&
    bodyText.trim().length > 0 &&
    examples.slice(0, needed).filter((value) => value?.trim()).length === needed;

  return (
    <section className="panel">
      <div className="panel-head">
        <h2>{t('utility.title')}</h2>
      </div>

      <p className="muted small">{t('utility.intro')}</p>

      <div className="filters">
        <DestinationFilter
          feature={DestinationFeature.INBOX}
          required
          value={pageId ? pageDestinationId(pageId) : ''}
          allLabel={t('utility.choosePage')}
          onChange={(id) => setPageId(pageIdOf(id))}
        />
      </div>

      {!pageId && (
        <div className="empty-state">
          <Icon.pages />
          <p>{t('utility.choosePage')}</p>
        </div>
      )}

      {/* A 403 here means the permission is not granted yet, which is the
          ordinary state before App Review — so it reads as the notice it is. */}
      {templates.isError && <ErrorNotice error={templates.error} />}
      {pageId && templates.isLoading && <div className="loading">{t('common.loading')}</div>}

      {pageId && templates.data && (
        <>
          <ul className="tpl-list">
            {rows.length === 0 && <li className="muted small">{t('utility.noTemplates')}</li>}
            {rows.map((template) => {
              const status = template.status.toUpperCase();
              return (
                <li key={template.id} className="card tpl-row">
                  <div className="tpl-id">
                    <strong dir="ltr">{template.name}</strong>
                    <span className="muted small" dir="ltr">
                      {template.language}
                    </span>
                  </div>

                  {/* Meta's word for the state, and Meta's reason when it said
                      no. Paraphrasing either would make the rejection harder to
                      act on, not easier. */}
                  <div className="tpl-state">
                    <span className={`tpl-badge tpl-${status.toLowerCase()}`}>
                      {statusLabel(status, t)}
                    </span>
                    {template.rejectionReason && (
                      <span className="muted small" dir="ltr">
                        {template.rejectionReason}
                      </span>
                    )}
                  </div>

                  <button
                    type="button"
                    className="ghost small"
                    disabled={remove.isPending}
                    onClick={() => remove.mutate(template.name)}
                  >
                    <Icon.trash />
                  </button>
                </li>
              );
            })}
          </ul>

          {remove.isError && <ErrorNotice error={remove.error} />}

          <form
            className="card stack"
            onSubmit={(event) => {
              event.preventDefault();
              if (ready) create.mutate();
            }}
          >
            <h3>{t('utility.newTemplate')}</h3>

            <label htmlFor="tpl-name">{t('utility.name')}</label>
            <input
              id="tpl-name"
              value={name}
              dir="ltr"
              placeholder="order_shipped"
              onChange={(event) => setName(event.target.value)}
            />
            <p className="muted small">{t('utility.nameHint')}</p>

            <label htmlFor="tpl-lang">{t('utility.language')}</label>
            <select
              id="tpl-lang"
              value={language}
              onChange={(event) => setLanguage(event.target.value)}
            >
              <option value="ar">العربية (ar)</option>
              <option value="en">English (en)</option>
            </select>

            <label htmlFor="tpl-header">{t('utility.header')}</label>
            <input
              id="tpl-header"
              value={headerText}
              onChange={(event) => setHeaderText(event.target.value)}
            />

            <label htmlFor="tpl-body">{t('utility.body')}</label>
            <textarea
              id="tpl-body"
              rows={3}
              value={bodyText}
              placeholder="طلبك رقم {{1}} تم شحنه"
              onChange={(event) => setBodyText(event.target.value)}
            />
            <p className="muted small">{t('utility.bodyHint')}</p>

            {/* Meta refuses a template whose examples do not match its
                placeholders, so the form asks for exactly as many as the text
                declares — and re-asks the moment the text changes. */}
            {Array.from({ length: needed }, (_, index) => (
              <input
                key={index}
                value={examples[index] ?? ''}
                placeholder={t('utility.examplePlaceholder').replace('{n}', String(index + 1))}
                onChange={(event) => {
                  const next = [...examples];
                  next[index] = event.target.value;
                  setExamples(next);
                }}
              />
            ))}

            <button type="submit" disabled={!ready || create.isPending}>
              <Icon.plus />
              {create.isPending ? t('utility.creating') : t('utility.create')}
            </button>

            {create.isError && <ErrorNotice error={create.error} />}
            {create.isSuccess && (
              <p className={create.data.status === 'REJECTED' ? 'warn small' : 'ok small'}>
                {statusLabel(create.data.status.toUpperCase(), t)}
                {create.data.rejectionReason ? ` — ${create.data.rejectionReason}` : ''}
              </p>
            )}
          </form>
        </>
      )}
    </section>
  );
}
